import { useEffect, useRef, useState } from 'react';
import { useAccount } from 'wagmi';

import { useWalletActions } from '#/features/wallet/wallet-context';
import { logException } from '#/features/logging/logging';
import { useParadexWebSocket } from '#/features/ws/ws-context';
import EthereumWallet from '#/features/ethereum-wallet/wallet';

import { useAuthActions, useAuthState } from './auth-context';

export function useAuthRecovery() {
  const [isRecoverable, setRecoverable] = useState(false);
  const wasCalledOnce = useRef(false);
  const ws = useParadexWebSocket();
  const [authState] = useAuthState();
  const authActions = useAuthActions();
  const walletActions = useWalletActions();

  useAccount({
    onConnect: ({ isReconnected }) => {
      if (IS_RUNNING_IN_CYPRESS === 'true') {
        setRecoverable(true);
        return;
      }

      // isReconnected will be true if the user reconnected via autoConnect.
      // isReconnected will be false in the scenario where user manually connects the wallet,
      // including the case where the user visits the app with "Remember Me" enabled,
      // but wallet is locked (e.g.locked MetaMask wallet).
      if (!isReconnected) return;
      setRecoverable(true);
    },
  });

  useEffect(() => {
    if (!isRecoverable) return;
    if (ws == null) return;

    if (wasCalledOnce.current) return;
    wasCalledOnce.current = true;

    if (authState.walletSeed == null) return;

    function signOut() {
      authActions.reset();
      walletActions.reset();
    }

    (async () => {
      const result = await walletActions.connectWallet();
      if (!result.ok) {
        if (result.error != null) {
          const error = new Error(
            'Error connecting wallet during auth recovery',
            { cause: result.error },
          );
          logException(error);
        }
        signOut();
        return;
      }

      const didUserChangeEthAccount =
        EthereumWallet.getAddress() !== authState.ethereumAddress;
      if (didUserChangeEthAccount) {
        signOut();
        return;
      }

      const resultInit = await walletActions.initializeStarknetWallet();
      if (!resultInit.ok) {
        if (resultInit.error != null) {
          const error = new Error(
            'Error initializing Starknet wallet during auth recovery',
            { cause: resultInit.error },
          );
          logException(error);
        }
        signOut();
        return;
      }

      const resultOnboarding = await walletActions.performOnboarding();
      if (!resultOnboarding.ok) {
        if (resultOnboarding.error != null) {
          const error = new Error(
            'Error onboarding Starknet wallet during auth recovery',
            { cause: resultOnboarding.error },
          );
          logException(error);
        }
        return;
      }

      const resultAuth = await walletActions.performAuth();
      if (!resultAuth.ok) {
        if (resultAuth.error != null) {
          const error = new Error(
            'Error authenticating Starknet wallet during auth recovery',
            { cause: resultAuth.error },
          );
          logException(error);
        }
        return; // eslint-disable-line no-useless-return
      }
    })().catch((err) => {
      const error = new Error(
        "Failed to recover session for a 'Remember Me' user",
        { cause: err },
      );
      logException(error);
      signOut();
    });
  }, [
    authActions,
    authState.ethereumAddress,
    authState.walletSeed,
    isRecoverable,
    walletActions,
    ws,
  ]);
}
